/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  EVENT_LOAD_FAIL,
  EVENT_LOAD_SUCCESS,
  EVENTS_FILTER_COUNTRY_VALUE_REMOVE,
  EVENTS_FILTER_PROVINCE_VALUE_REMOVE,
  EVENTS_FILTER_VALUE_ADD,
  EVENTS_FILTER_VALUE_REMOVE,
  EVENTS_FILTER_VALUE_UPDATE,
  EVENTS_RESET,
  EVENTS_SEARCH,
  EVENTS_SEARCH_FAIL,
  EVENTS_SEARCH_SUCCESS,
  UPDATE_EVENT_LISTING_CURRENT_PAGE,
  UPDATE_EVENT_LISTING_PAGE_SIZE,
  UPDATE_EVENT_STATUS,
  UPDATE_EVENT_STATUS_FAIL,
  UPDATE_EVENT_STATUS_SUCCESS,
  UPDATE_SELECTED_EVENT_ID,
} from "../../Constants/ActionTypes";
import {
  defaultEventFilterValues,
  defaultListingPageSize,
  EVENT_COUNTRY_REGION,
  EVENT_STATE_PROVINCE,
} from "../../Constants/EventConstants";
import { IEventUserProfileRole } from "../../Interfaces/Event/IEventUserProfileRole";
import {
  EventActionTypes,
  IEventsFilterValueAdd,
  IEventsFilterValueRemove,
  IEventsFilterValueUpdate,
  ILoadEventFail,
  ILoadEventSuccess,
  ISearchEvents,
  ISearchEventsFail,
  ISearchEventsSuccess,
  IUpdateCurrentPage,
  IUpdateEventStatusFail,
  IUpdateEventStatusSuccess,
  IUpdatePageSize,
  IUpdateSelectedEventId,
} from "./EventAction";
import { IEventState } from "./IEventState";

const INITIAL_USER_PROFILE_ROLE: IEventUserProfileRole = {
  userProfileId: 0,
  userProfileFirstName: "",
  userProfileLastName: "",
  userProfilePrimaryEmail: "",
  userProfileStudentPartnerEmail: "",
  userProfileHeadline: "",
  eventRoleId: 0,
  eventRoleName: "",
  tenants: [""],
  userProfileLevelStatus: "",
  isHighImpact: false,
};

const INITIAL_STATE: IEventState = {
  events: [],
  eventsFilterValues: defaultEventFilterValues,
  loadEventsError: "",
  userProfileId: 0,
  isLoadingEvents: true,
  isLoadingSelectedEvent: false,
  isUpdatingStatus: false,
  loadSelectedEventError: "",
  selectedEventId: "",
  updateEventStatusError: "",
  currentPage: 1,
  pageSize: defaultListingPageSize,
  searchText: "",
  filteredCount: 0,
  selectedEvent: {
    actualAttendance: 0,
    addressLatitude: 0,
    addressLongitude: 0,
    allDay: false,
    city: "",
    createdBy: "",
    dateLastReviewed: new Date(),
    dateCreated: new Date(),
    dateEnd: new Date(),
    dateStart: new Date(),
    description: "",
    eventCountryOrRegionNames: [""],
    streetAddress: "",
    buildingName: "",
    roomNo: "",
    eventFormat: "",
    eventIdentifier: "",
    eventOrganizerUserProfileRole: INITIAL_USER_PROFILE_ROLE,
    eventSkillNames: [""],
    eventStatusName: "",
    eventTargetAudienceNames: [],
    eventType: "",
    eventUri: "",
    eventUserProfileContributorRole: [INITIAL_USER_PROFILE_ROLE],
    expectedAttendance: 0,
    icsuri: "",
    id: 0,
    isCateringRequested: false,
    isFirstParty: false,
    isHighImpact: false,
    isPrivate: false,
    isReimbursementRequested: false,
    microsoftContactOwnerEmailAddress: "",
    microsoftContactOwnerFirstName: "",
    microsoftContactOwnerLastName: "",
    photosUrl: "",
    postEventDetails: "",
    programProfileEvent: [{ id: 0, accepted: false, eventRoleName: "" }],
    reach: 0,
    spotlight: false,
    stateOrProvince: "",
    technologyFocusAreaName: "",
    tenantName: "",
    timeEnd: "",
    timeStart: "",
    title: "",
    userRole: "",
    videoUri: "",
  },
};

const applyLoadEvent = (state: IEventState, action: ILoadEventSuccess): IEventState => ({
  ...state,
  selectedEvent: action.event,
  isLoadingSelectedEvent: false,
  loadSelectedEventError: "",
  updateEventStatusError: "",
});

const applyLoadFailEvent = (state: IEventState, action: ILoadEventFail): IEventState => ({
  ...state,
  loadSelectedEventError: action.loadSelectedEventError,
  isLoadingSelectedEvent: false,
});

const addEventsFilterValue = (state: IEventState, action: IEventsFilterValueAdd): IEventState => ({
  ...state,
  eventsFilterValues: state.eventsFilterValues.concat([action.filterValue]),
});

const removeEventsFilterValue = (
  state: IEventState,
  action: IEventsFilterValueRemove
): IEventState => ({
  ...state,
  eventsFilterValues: state.eventsFilterValues.filter(
    (item) =>
      !(item.fieldName === action.filterValue.fieldName && item.value === action.filterValue.value)
  ),
});

const removeEventsFilterProvinceValue = (state: IEventState): IEventState => ({
  ...state,
  eventsFilterValues: state.eventsFilterValues.filter(
    (item) => !(item.fieldName === EVENT_STATE_PROVINCE)
  ),
});

const removeEventsFilterCountryValue = (state: IEventState): IEventState => ({
  ...state,
  eventsFilterValues: state.eventsFilterValues.filter(
    (item) => !(item.fieldName === EVENT_COUNTRY_REGION)
  ),
});

const updateEventsFilterValue = (
  state: IEventState,
  action: IEventsFilterValueUpdate
): IEventState => ({
  ...state,
  eventsFilterValues: state.eventsFilterValues
    .filter((item) => !(item.fieldName === action.filterValue.fieldName))
    .concat([action.filterValue]),
});

const applySearchEvents = (state: IEventState, action: ISearchEvents): IEventState => ({
  ...state,
  isLoadingEvents: true,
  searchText: action.searchText,
  currentPage: action.searchPage,
  loadEventsError: "",
});

const applySearchEventsSuccess = (
  state: IEventState,
  action: ISearchEventsSuccess
): IEventState => ({
  ...state,
  events: action.events,
  currentPage: action.searchPage,
  isLoadingEvents: false,
  filteredCount: action.filteredCount,
  loadEventsError: "",
});

const applySearchEventsFail = (state: IEventState, action: ISearchEventsFail): IEventState => ({
  ...state,
  loadEventsError: action.error,
  isLoadingEvents: false,
});

const applySelectedEvent = (state: IEventState, action: IUpdateSelectedEventId): IEventState => ({
  ...state,
  selectedEventId: action.selectedEventId,
  updateEventStatusError: "",
  isLoadingSelectedEvent: true,
});

const applyUpdateEventStatus = (state: IEventState): IEventState => ({
  ...state,
  isUpdatingStatus: true,
  updateEventStatusError: "",
});

const applyUpdateEventStatusSuccess = (
  state: IEventState,
  action: IUpdateEventStatusSuccess
): IEventState => ({
  ...state,
  selectedEvent: action.event,
  updateEventStatusError: "",
  isUpdatingStatus: false,
});

const applyUpdateEventStatusFail = (
  state: IEventState,
  action: IUpdateEventStatusFail
): IEventState => ({
  ...state,
  updateEventStatusError: action.updateEventStatusError,
  isUpdatingStatus: false,
});

const applyUpdateCurrentPage = (state: IEventState, action: IUpdateCurrentPage): IEventState => ({
  ...state,
  currentPage: action.currentPage,
});

const applyUpdatePageSize = (state: IEventState, action: IUpdatePageSize): IEventState => ({
  ...state,
  pageSize: action.pageSize,
});

const applyEventsReset = (): IEventState => INITIAL_STATE;

function eventReducer(state = INITIAL_STATE, action: EventActionTypes): IEventState {
  switch (action.type) {
    case EVENT_LOAD_SUCCESS: {
      return applyLoadEvent(state, action);
    }
    case EVENT_LOAD_FAIL: {
      return applyLoadFailEvent(state, action);
    }
    case EVENTS_FILTER_VALUE_ADD: {
      return addEventsFilterValue(state, action);
    }
    case EVENTS_FILTER_VALUE_REMOVE: {
      return removeEventsFilterValue(state, action);
    }
    case EVENTS_FILTER_COUNTRY_VALUE_REMOVE: {
      return removeEventsFilterCountryValue(state);
    }
    case EVENTS_FILTER_PROVINCE_VALUE_REMOVE: {
      return removeEventsFilterProvinceValue(state);
    }
    case EVENTS_FILTER_VALUE_UPDATE: {
      return updateEventsFilterValue(state, action);
    }
    case EVENTS_SEARCH: {
      return applySearchEvents(state, action);
    }
    case EVENTS_SEARCH_FAIL: {
      return applySearchEventsFail(state, action);
    }
    case EVENTS_SEARCH_SUCCESS: {
      return applySearchEventsSuccess(state, action);
    }
    case UPDATE_SELECTED_EVENT_ID: {
      return applySelectedEvent(state, action);
    }
    case UPDATE_EVENT_STATUS: {
      return applyUpdateEventStatus(state);
    }
    case UPDATE_EVENT_STATUS_SUCCESS: {
      return applyUpdateEventStatusSuccess(state, action);
    }
    case UPDATE_EVENT_STATUS_FAIL: {
      return applyUpdateEventStatusFail(state, action);
    }
    case UPDATE_EVENT_LISTING_CURRENT_PAGE: {
      return applyUpdateCurrentPage(state, action);
    }
    case UPDATE_EVENT_LISTING_PAGE_SIZE: {
      return applyUpdatePageSize(state, action);
    }
    case EVENTS_RESET: {
      return applyEventsReset();
    }
    default:
      return state;
  }
}

export default eventReducer;
